import {Image} from 'coreClasses/images/Image';
import ContentfulAsset from './ContentfulAsset';
import ViewportMgr from 'coreClasses/view/ViewportMgr';

/**
 * Represents ContentfulImage
 */
class ContentfulImage implements Image {
	private readonly title: string;
	private breakpointImages: object = {};
	private firstImage: ContentfulAsset;

	/**
	 * @constructor
	 * @param {String} title - contentful image data
	 * @param {Object} breakpointImagesProps - contentful image data
	 * @param {Boolean} lossless - true if should return a lossless image
	 */
	constructor(title: string, breakpointImagesProps: object, lossless: boolean = false) {
		this.title = title;

		Object.keys(breakpointImagesProps)
			.forEach((breakPoint) => {
				if (breakpointImagesProps[breakPoint]) {
					const asset = new ContentfulAsset(breakpointImagesProps[breakPoint], lossless);

					this.breakpointImages[breakPoint] = asset;

					if (!this.firstImage) {
						this.firstImage = asset;
					}
				}
			});
	}

	/**
	 * Returns the url recalculated based on the given breakpoint and dpi
	 * @param {string} breakPoint the breakpoint key
	 * @param {number} dpi the desired dpi
	 * @return {string} the calculated image url
	 */
	getUrl(breakPoint: string = null, dpi: number = 1) : string {
		return this.findNearestAvailableBreakpoint(breakPoint)?.getUrl(breakPoint, dpi);
	}

	/**
	 * Returns the placeholder url recalculated based on the given breakpoint and dpi
	 * @param {string} breakPoint the breakpoint key
	 * @param {number} dpi the desired dpi
	 * @return {string} the calculated image url
	 */
	getPlaceholderUrl(breakPoint: string = null, dpi: number = 1) : string {
		return this.findNearestAvailableBreakpoint(breakPoint).getPlaceholderUrl(breakPoint, dpi);
	}

	/**
	 * Returns the image width as string
	 * @return {Number} the image width
	 */
	getWidth(breakPoint: string) : number {
		return breakPoint
			? this.findNearestAvailableBreakpoint(breakPoint).getWidth()
			: this.firstImage.getWidth();
	}

	/**
	 * Returns the image height as string
	 * @return {Number} the image height
	 */
	getHeight(breakPoint: string) : number {
		return breakPoint
			? this.findNearestAvailableBreakpoint(breakPoint).getHeight()
			: this.firstImage.getHeight();
	}

	/**
	 * Returns the image title
	 * @return {string} the image title
	 */
	getTitle() : string {
		return this.title;
	}

	/**
	 * Returns the image alternative text
	 * @return {string} the image alternative text
	 */
	getAlt() : string {
		return this.firstImage?.getDescription() ?? this.title ?? '';
	}

	/**
	 * returns the nearest available breakpoint.
	 * @param breakPoint
	 * @private
	 */
	private findNearestAvailableBreakpoint(breakPoint: string): ContentfulAsset {
		const systemBreakPoints = ViewportMgr.getBreakpoints();
		const breakPointIndex = systemBreakPoints.indexOf(breakPoint);

		for (let i = (breakPointIndex + 1); i--; i >= 0) {
			const currentBreakpoint = systemBreakPoints[i];

			if (currentBreakpoint in this.breakpointImages) {
				return this.breakpointImages[currentBreakpoint];
			}
		}

		const breakpointValues = Object.values(this.breakpointImages);

		return breakpointValues[breakpointValues.length - 1];
	}
}

export default ContentfulImage;
